import { useRef, useEffect, useMemo } from "react";
import {
  Group,
  Vector2,
  BufferGeometry,
  BufferAttribute,
  ShaderMaterial,
  Points,
  DataTexture,
  RGBAFormat,
} from "three";
import { useThree } from "@react-three/fiber";
import { scaleSequential } from "d3-scale";
import { interpolateTurbo } from "d3-scale-chromatic";
import { rgb } from "d3-color";
import { useAtom } from "jotai";

import { type View, BED_DIMENSIONS } from "_/state/viewer";

import pointsVert from "./glsl/points.vert";
import pointsFrag from "./glsl/points.frag";

function generateLookupTexture() {
  // Color scale used to map sensor values to colors
  const scale = scaleSequential(interpolateTurbo).domain([0, 1]);

  // Generate a lookup texture to use the color scale in the shader
  const textureWidth = 256;
  const data = new Uint8Array(textureWidth * 4); // RGBA

  for (let i = 0; i < textureWidth; i++) {
    const color = rgb(scale(i / textureWidth));
    const offset = i * 4;
    data[offset] = color.r;
    data[offset + 1] = color.g;
    data[offset + 2] = color.b;
    data[offset + 3] = 255;
  }

  const lookupTexture = new DataTexture(data, textureWidth, 1, RGBAFormat);
  lookupTexture.needsUpdate = true;
  return lookupTexture;
}

type SimViewProps = {
  view: View<"optimize">;
};

export const SimView = ({ view }: SimViewProps) => {
  const { invalidate } = useThree();

  const groupRef = useRef<Group>(null);

  const [viewControls] = useAtom(view.controlsAtom);

  const geometries = useMemo(() => {
    const originalGeometry = new BufferGeometry();

    originalGeometry.setAttribute(
      "position",
      new BufferAttribute(view.data.original.positions, 3)
    );

    originalGeometry.setAttribute(
      "layerWelding",
      new BufferAttribute(view.data.original.layerWelding, 1)
    );

    originalGeometry.setAttribute(
      "previousLayerTemperature",
      new BufferAttribute(view.data.original.previousLayerTemperature, 1)
    );

    const geometriesInner: {
      original: BufferGeometry;
      optimized?: BufferGeometry;
    } = {
      original: originalGeometry,
    };

    if (view.data.optimized) {
      const optimizedGeometry = new BufferGeometry();

      optimizedGeometry.setAttribute(
        "position",
        new BufferAttribute(view.data.optimized.positions, 3)
      );

      optimizedGeometry.setAttribute(
        "layerWelding",
        new BufferAttribute(view.data.optimized.layerWelding, 1)
      );

      optimizedGeometry.setAttribute(
        "previousLayerTemperature",
        new BufferAttribute(view.data.optimized.previousLayerTemperature, 1)
      );

      geometriesInner.optimized = optimizedGeometry;
    }

    return geometriesInner;
  }, [view.data]);

  const material = useMemo(
    () =>
      new ShaderMaterial({
        uniforms: {
          lookupTexture: { value: generateLookupTexture() },
          zRange: { value: new Vector2() },
          valueRange: {
            value: new Vector2(),
          },
          colorLimit: {
            value: new Vector2(),
          },
          snapshot: {
            value: 0,
          },
        },
        vertexShader: pointsVert,
        fragmentShader: pointsFrag,
      }),
    []
  );

  const points = useMemo(() => {
    geometries.original.computeBoundingBox();

    const bbox = geometries.original.boundingBox;

    // Compute the offset by finding the center of the bounding box (in x and y)
    const offset = bbox
      ? bbox.min.clone().lerp(bbox.max, 0.5)
      : new Vector2(0, 0);

    const pointsInner = new Points(geometries.original, material);

    // Set the position of the points to be centered around the origin
    pointsInner.position.set(-offset.x, -offset.y, 0);

    return pointsInner;
  }, [geometries, material]);

  useEffect(() => {
    return () => {
      geometries.original.dispose();
      geometries.optimized?.dispose();
      // No good types for uniforms, so need cast
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      (material.uniforms.lookupTexture.value as { dispose(): void }).dispose();
      material.dispose();
    };
  }, [geometries, material]);

  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.add(points);
    }
  }, [points]);

  useEffect(() => {
    const sourceGeometry = geometries[viewControls.source];
    if (sourceGeometry) {
      points.geometry = sourceGeometry;
      invalidate();
    }
  }, [geometries, points, viewControls.source, invalidate]);

  // Update shader material uniforms based on control changes
  useEffect(() => {
    const { zRange } = viewControls.values;
    const { colorRange, valueRange } =
      viewControls.values[viewControls.snapshot];

    // We don't have good type information for the uniforms, so cast them.
    /* oxlint-disable typescript/no-unsafe-type-assertion */
    type XY = { x: number; y: number };
    (material.uniforms.zRange.value as XY).x = zRange[0];
    (material.uniforms.zRange.value as XY).y = zRange[1];
    (material.uniforms.valueRange.value as XY).x = valueRange[0];
    (material.uniforms.valueRange.value as XY).y = valueRange[1];
    (material.uniforms.colorLimit.value as XY).x = colorRange[0];
    (material.uniforms.colorLimit.value as XY).y = colorRange[1];
    /* oxlint-enable typescript/no-unsafe-type-assertion */
    material.uniformsNeedUpdate = true;
    invalidate();
  }, [viewControls.snapshot, viewControls.values, material, invalidate]);

  useEffect(() => {
    if (viewControls.snapshot) {
      material.uniforms.snapshot.value =
        viewControls.snapshot === "previousLayerTemperature" ? 0 : 1;
      material.uniformsNeedUpdate = true;
      invalidate();
    }
  }, [viewControls.snapshot, material, invalidate]);

  return (
    <group
      ref={groupRef}
      // Position the group at the center of the bed (which is the default lookAt target of the camera)
      position={[BED_DIMENSIONS.width / 2, BED_DIMENSIONS.height / 2, 0]}
    />
  );
};
